/**
 * CanvasCoachPanel Component
 *
 * A clean, intuitive chat interface for canvas coaching.
 * Features:
 * - Simple chat interface that works at any width
 * - Progress shown as a collapsible section at top
 * - Quick reply pills
 * - Streaming support
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import {
  Send,
  Loader2,
  AlertCircle,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Layout,
  X,
  BarChart3,
  Plus,
  Tag,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  useCanvasCoach,
  type ChatMessage,
  type CanvasType,
  type CoachMode,
  type ExtractedEntity,
} from '@/hooks/useCanvasCoach'
import { useAIStore } from '@/stores/aiStore'

/** Control functions for entity management from parent */
export interface EntityControlFunctions {
  approveEntity: (entity: ExtractedEntity) => void
  dismissEntity: (entity: ExtractedEntity) => void
}

interface CanvasCoachPanelProps {
  accountId: string
  projectId: string
  instanceId: string | undefined
  canvasType: CanvasType
  currentSection?: string
  /** Current canvas content as string - used by AI to detect duplicates */
  canvasContent?: string
  onSectionFocus?: (sectionId: string) => void
  onAddToCanvas?: (entity: ExtractedEntity, sectionId: string) => void
  /** Called when extracted entities change - allows parent to show them on canvas */
  onEntitiesChange?: (entities: ExtractedEntity[]) => void
  /** Called to provide parent with entity control functions */
  onControlsReady?: (controls: EntityControlFunctions) => void
  /** If true, show entities in the panel; if false, parent handles display */
  showEntitiesInPanel?: boolean
  className?: string
}

export function CanvasCoachPanel({
  accountId,
  projectId,
  instanceId,
  canvasType,
  currentSection,
  canvasContent,
  onSectionFocus,
  onAddToCanvas,
  onEntitiesChange,
  onControlsReady,
  showEntitiesInPanel = true,
  className,
}: CanvasCoachPanelProps) {
  const { apiKey, conversationMode } = useAIStore()
  const [inputValue, setInputValue] = useState('')
  const [showProgress, setShowProgress] = useState(false)
  const [showEntitiesBanner, setShowEntitiesBanner] = useState(true)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const {
    messages,
    sectionCoverage,
    isLoading,
    isStreaming,
    error,
    quickActions,
    suggestedNextSection,
    suggestionMessage,
    extractedEntities,
    sendMessage,
    handleQuickAction,
    setMode,
    dismissSuggestion,
    addEntityToCanvas,
    dismissEntity,
  } = useCanvasCoach(accountId, projectId, instanceId, canvasType, canvasContent)

  // Sync mode with store
  useEffect(() => {
    setMode((conversationMode as CoachMode) || 'organic')
  }, [conversationMode, setMode])

  // Notify parent when entities change
  useEffect(() => {
    onEntitiesChange?.(extractedEntities)
  }, [extractedEntities, onEntitiesChange])

  // Provide entity control functions to parent
  useEffect(() => {
    onControlsReady?.({
      approveEntity: addEntityToCanvas,
      dismissEntity: dismissEntity,
    })
  }, [onControlsReady, addEntityToCanvas, dismissEntity])


  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Handle send
  const handleSend = useCallback(async () => {
    if (!inputValue.trim() || isLoading || isStreaming) return
    const content = inputValue.trim()
    setInputValue('')
    await sendMessage(content, currentSection)
  }, [inputValue, isLoading, isStreaming, sendMessage, currentSection])

  // Handle key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Handle adding entity to canvas
  const handleAddEntity = useCallback((entity: ExtractedEntity) => {
    const sectionId = entity.sectionId || currentSection || 'customer-segments'
    onAddToCanvas?.(entity, sectionId)
    addEntityToCanvas(entity)
  }, [onAddToCanvas, addEntityToCanvas, currentSection])

  // Canvas type names for display
  const canvasNames: Record<CanvasType, string> = {
    'bmc': 'Business Model',
    'vpc': 'Value Proposition',
    'positioning': 'Positioning',
    'psc': 'Problem Statement',
    'persona': 'Persona',
  }

  // Calculate overall progress
  const overallProgress = sectionCoverage?.overallProgress ?? 0

  // Not configured message
  if (!apiKey) {
    return (
      <div className={cn('flex flex-col bg-white h-full', className)}>
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-warm-100 flex items-center justify-center mb-3">
            <AlertCircle className="w-6 h-6 text-warm-500" />
          </div>
          <h3 className="text-base font-semibold text-warm-700 mb-1">AI Not Configured</h3>
          <p className="text-sm text-text-muted">
            Add your Claude API key in Settings.
          </p>
        </div>
      </div>
    )
  }

  // No instance message
  if (!instanceId) {
    return (
      <div className={cn('flex flex-col bg-white h-full', className)}>
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-solution/10 flex items-center justify-center mb-3">
            <Layout className="w-6 h-6 text-solution" />
          </div>
          <h3 className="text-base font-semibold text-warm-700 mb-1">Select a Canvas</h3>
          <p className="text-sm text-text-muted">
            Open a canvas to start coaching.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col bg-white h-full', className)}>
      {/* Compact Header */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-warm-200 bg-gradient-to-r from-warm-50 to-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-solution to-solution/80 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-warm-800">Canvas Coach</h2>
              {/* Compact Progress Dots */}
              {sectionCoverage && (
                <div className="flex items-center gap-0.5 mt-0.5" title="Section progress">
                  {sectionCoverage.sections.map(({ section, coverage }) => (
                    <div
                      key={section.id}
                      title={`${section.name}: ${coverage?.level || 'not started'}`}
                      className={cn(
                        'w-2 h-2 rounded-full transition-colors',
                        coverage?.level === 'validated' && 'bg-success',
                        coverage?.level === 'explored' && 'bg-business',
                        coverage?.level === 'mentioned' && 'bg-warning',
                        (!coverage || coverage.level === 'not_started') && 'bg-warm-200'
                      )}
                    />
                  ))}
                </div>
              )}
              {!sectionCoverage && (
                <p className="text-xs text-text-muted">{canvasNames[canvasType]}</p>
              )}
            </div>
          </div>

          {/* Progress Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowProgress(!showProgress)}
            className="h-7 px-2 text-xs gap-1"
          >
            <BarChart3 className="h-3 w-3" />
            <span className="font-medium">{Math.round(overallProgress)}%</span>
            {showProgress ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </Button>
        </div>

        {/* Collapsible Progress Section */}
        {showProgress && sectionCoverage && (
          <div className="mt-3 pt-3 border-t border-warm-100">
            <div className="space-y-1.5">
              {sectionCoverage.sections.slice(0, 5).map(({ section, coverage }) => (
                <button
                  key={section.id}
                  onClick={() => onSectionFocus?.(section.id)}
                  className="w-full flex items-center gap-2 text-xs hover:bg-warm-50 rounded p-1 -mx-1 transition-colors"
                >
                  <div className="flex-1 text-left text-warm-600 truncate">
                    {section.name}
                  </div>
                  <div className={cn(
                    'px-1.5 py-0.5 rounded text-[10px] font-medium',
                    coverage?.level === 'validated' && 'bg-success-bg text-success-fg',
                    coverage?.level === 'explored' && 'bg-business/10 text-business-700',
                    coverage?.level === 'mentioned' && 'bg-warning-bg text-warning-fg',
                    (!coverage || coverage.level === 'not_started') && 'bg-warm-100 text-warm-500'
                  )}>
                    {coverage?.level || 'not started'}
                  </div>
                </button>
              ))}
              {sectionCoverage.sections.length > 5 && (
                <p className="text-[10px] text-text-muted text-center">
                  +{sectionCoverage.sections.length - 5} more sections
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Suggestion Banner */}
      {suggestionMessage && (
        <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 bg-primary/5 border-b border-primary/10">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Sparkles className="h-3 w-3 text-primary flex-shrink-0" />
            <span className="text-xs text-primary truncate">{suggestionMessage}</span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {suggestedNextSection && (
              <Button
                size="sm"
                variant="ghost"
                className="h-6 text-[10px] text-primary hover:bg-primary/10 px-2"
                onClick={() => {
                  onSectionFocus?.(suggestedNextSection)
                  dismissSuggestion()
                }}
              >
                Go
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0"
              onClick={dismissSuggestion}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}

      {/* Extracted Entities Banner - only show if showEntitiesInPanel is true */}
      {showEntitiesInPanel && extractedEntities.length > 0 && showEntitiesBanner && (
        <div className="flex-shrink-0 border-b border-warm-200 bg-warm-50/80">
          <div className="px-3 py-2">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 text-xs font-medium text-warm-700">
                <Tag className="h-3 w-3" />
                <span>Detected ({extractedEntities.length})</span>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="h-5 w-5 p-0"
                onClick={() => setShowEntitiesBanner(false)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
            <div className="space-y-1.5 max-h-32 overflow-y-auto">
              {extractedEntities.map((entity, index) => (
                <div
                  key={`${entity.label}-${index}`}
                  className="flex items-start gap-2 text-xs bg-white rounded-md p-2 border border-warm-200"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-warm-800 truncate">
                      {entity.label}
                    </div>
                    {entity.description && (
                      <div className="text-warm-500 text-[10px] truncate">
                        {entity.description}
                      </div>
                    )}
                    <div className="flex items-center gap-1 mt-1">
                      <span className="px-1.5 py-0.5 bg-primary/10 text-primary rounded text-[10px]">
                        {entity.type}
                      </span>
                      {entity.sectionId && (
                        <span className="px-1.5 py-0.5 bg-warm-100 text-warm-600 rounded text-[10px]">
                          → {entity.sectionId.replace(/-/g, ' ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-[10px] text-solution hover:bg-solution/10"
                      onClick={() => handleAddEntity(entity)}
                    >
                      <Plus className="h-3 w-3 mr-0.5" />
                      Add
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 text-warm-400 hover:text-warm-600"
                      onClick={() => dismissEntity(entity)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Collapsed entities indicator - only show if showEntitiesInPanel is true */}
      {showEntitiesInPanel && extractedEntities.length > 0 && !showEntitiesBanner && (
        <button
          onClick={() => setShowEntitiesBanner(true)}
          className="flex-shrink-0 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-solution/5 border-b border-solution/20 text-xs text-solution hover:bg-solution/10 transition-colors"
        >
          <Tag className="h-3 w-3" />
          <span>{extractedEntities.length} entities detected - click to show</span>
        </button>
      )}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-0">
        {/* Welcome message if no messages */}
        {messages.length === 0 && !isLoading && (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <div className="w-12 h-12 rounded-full bg-solution/10 flex items-center justify-center mb-3">
              <Sparkles className="w-6 h-6 text-solution" />
            </div>
            <h3 className="text-sm font-semibold text-warm-700 mb-1">
              Let's fill your canvas!
            </h3>
            <p className="text-xs text-text-muted leading-relaxed">
              Tell me about your business and I'll help populate each section.
            </p>
          </div>
        )}

        {/* Message list */}
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}

        {/* Quick actions */}
        {quickActions.length > 0 && !isStreaming && (
          <div className="flex flex-wrap gap-1.5">
            {quickActions.map((action, index) => (
              <button
                key={index}
                onClick={() => handleQuickAction(action)}
                disabled={isLoading || isStreaming}
                className={cn(
                  'px-2.5 py-1 rounded-full text-xs font-medium transition-all',
                  // Different colors based on action type
                  action.type === 'add_examples' && 'bg-success-bg text-success-fg border border-success/20 hover:bg-success-bg/80',
                  action.type === 'show_more' && 'bg-business/10 text-business-700 border border-business/20 hover:bg-business/15',
                  action.type === 'navigate_section' && 'bg-market/10 text-market-700 border border-market/20 hover:bg-market/15',
                  action.type === 'skip' && 'bg-info-bg text-info-fg border border-info/20 hover:bg-info-bg/80',
                  action.type === 'text' && 'bg-solution/10 text-solution border border-solution/20 hover:bg-solution/20',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="flex items-start gap-2 text-xs text-danger-fg bg-danger-bg px-3 py-2 rounded-lg">
            <AlertCircle className="h-3 w-3 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="flex-shrink-0 p-3 border-t border-warm-200 bg-warm-50/50">
        <div className="relative">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isStreaming
                ? 'Thinking...'
                : currentSection
                  ? `About ${currentSection.replace(/-/g, ' ')}...`
                  : 'Tell me about your business...'
            }
            rows={2}
            className="w-full resize-none rounded-lg border border-warm-200 px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/30 placeholder:text-warm-400 bg-white"
            disabled={isLoading || isStreaming}
          />
          <Button
            size="sm"
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading || isStreaming}
            className="absolute right-1.5 bottom-1.5 h-7 w-7 p-0 rounded-md"
          >
            {isLoading || isStreaming ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Send className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
        <div className="mt-1.5 flex items-center justify-between text-[10px] text-warm-400">
          <span>Enter to send • Shift+Enter for new line</span>
          {currentSection && (
            <span className="text-primary font-medium">
              {currentSection.replace(/-/g, ' ')}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

// Compact Message Bubble Component
function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user'

  return (
    <div className={cn('flex gap-2', isUser ? 'flex-row-reverse' : 'flex-row')}>
      {/* Avatar - only for assistant */}
      {!isUser && (
        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-solution to-solution/80 flex items-center justify-center flex-shrink-0">
          <Layout className="w-3 h-3 text-white" />
        </div>
      )}

      {/* Message content */}
      <div
        className={cn(
          'max-w-[85%] rounded-xl px-3 py-2',
          isUser && 'bg-warm-700 text-white rounded-br-sm',
          !isUser && 'bg-warm-100 text-warm-800 rounded-bl-sm'
        )}
      >
        <div className={cn(
          'text-sm leading-relaxed whitespace-pre-wrap',
          message.isStreaming && 'animate-pulse'
        )}>
          {message.content || (message.isStreaming ? '...' : '')}
          {message.isStreaming && message.content && (
            <span className="inline-block w-1 h-3 bg-solution ml-0.5 animate-pulse rounded-sm" />
          )}
        </div>
      </div>
    </div>
  )
}
